// @ts-nocheck @todo fix when cart is implemented

import _, {concat, uniqBy, reduce} from "lodash";
import {defineStore} from 'pinia';
import {computed, type ComputedRef, onMounted, reactive, ref} from "vue";
import type {Ref, UnwrapNestedRefs} from 'vue';
import type {AxiosRequestConfig, AxiosResponse} from "axios";
import type {CartCalculatorResponse} from "../../responses/Site/CartCalculatorResponse";
import type {CartCalculatorLineResponse} from "../../responses/Site/CartCalculatorLineResponse";
import {api} from "../scripts/api";
import type {ProductVariantResponse} from "../../responses/ProductVariantResponse.ts";
import type {OrderLineResponse} from "../../responses/OrderLineResponse.ts";
import CartDiscount from "../Components/Cart/CartDiscount.vue";
import {wait} from "../../utils/wait.ts";
import {isIframe} from "../../utils/iframe.ts";
import type {DeliveryMethodResponse} from "../../responses/DeliveryMethodResponse.ts";
import mitt, {type Emitter} from "mitt";

type Events = {
    afterAdding: void
}

export const useCartStore = defineStore('cart', () => {
    const isLoading: Ref<boolean> = ref(true) // Will only show specific small loading skeletons for example prices
    const isLoadingFull: Ref<boolean> = ref(true) // Will cause full page shift and move full view in loading
    const uuid: Ref<string | null> = ref(null)
    const cart: Ref<CartCalculatorResponse | null> = ref(null);
    const deliveryMethod: Ref<string | null> = ref(null);
    const deliveryMethods: Ref<DeliveryMethodResponse | null> = ref(null);

    // const data: Ref<Array<any>> = ref([]); // @todo cartDataResource?
    // const cartItems: Ref<CartCalculatorLineResponse[]> = ref([]);
    // const serviceItems: Ref<Array<any>> = ref([]);
    // const lastOrderLine: Ref<OrderLineResponse> = ref({});
    const delivery_country_code: Ref<string> = ref("");
    const events: Emitter<Events> = mitt<Events>();


    const updateQuantityRequest: UnwrapNestedRefs<{
        timeout: number | null
        abort: AbortController | null
    }> = reactive({
        timeout: null,
        abort: null,
    })

    const items: ComputedRef<CartCalculatorLineResponse[]> = computed(() => cart.value?.lines ?? [])
    const itemCount: ComputedRef<number> = computed(() => items.value.reduce((nr, curr) => nr + curr.model.quantity, 0) ?? 0)

    const getDiscounts = ():  | null => {
        if (!cart || !cart.value?.discount_models) return null;
        return cart.value.discount_models.length === 0 ? [] : uniqBy((cart.value.discount_models), "code");
    }



    const loadCartAbortController: Ref<AbortController | null> = ref(null);
    const loadCart = async () => {
        return new Promise(async (resolve, reject) => {
            isLoading.value = true

            if (loadCartAbortController.value !== null) {
                loadCartAbortController.value.abort()
            }

            const abortController: AbortController = new AbortController()
            loadCartAbortController.value = abortController

            try {
                const config: AxiosRequestConfig = {
                    signal: abortController.signal
                }

                const response: void | AxiosResponse<{
                    cart: CartCalculatorResponse,
                    data: { [key: string]: string },
                    delivery_country_code: string
                }> = await api().post(
                    '/site/cart/',
                    {
                        cart: uuid.value ?? null,
                        delivery_country_code: delivery_country_code.value
                    },
                    config
                ).then(response => {
                    cart.value = response.data.cart
                    deliveryMethod.value = cart.value.data?.delivery_method ?? null;
                    delivery_country_code.value = response.data.delivery_country_code;

                    isLoading.value = false

                    if (isLoadingFull.value) {
                        isLoadingFull.value = false
                    }

                    resolve(response.data)
                })
            } catch (e) {
                isLoading.value = false

                if (isLoadingFull.value) {
                    isLoadingFull.value = false
                }

                reject(e)
            }
        });
    }

    const addItem = (payload) => {
        payload.cart = uuid.value;
        return new Promise((resolve, reject) => {
            api().post("/site/cart/line/", payload)
                .then(response => {
                    if (response.data?.uuid) {
                        uuid.value = response.data.uuid
                    }
                    loadCart();
                    resolve(response);

                    events.emit('afterAdding');
                })
                .catch(error => {
                    reject(error);
                });
        });
    }

    const updateQuantity = async <T>(payload): Promise<T> => {
        return new Promise<T>(async (resolve, reject): Promise<void> => {
            payload.cart = uuid.value;
            if (updateQuantityRequest.abort !== null) {
                updateQuantityRequest.abort.abort()
            }

            const abortController: AbortController = new AbortController()
            updateQuantityRequest.abort = abortController

            try {
                const config: AxiosRequestConfig = {
                    signal: abortController.signal
                }

                const response: void | AxiosResponse<T> = await api().post(
                    '/site/cart/line/update-quantity/',
                    _.merge(payload, {_method: "PUT"}),
                    config
                ).then(response => {
                    loadCart();

                    resolve(response.data)
                })
            } catch (e) {
                reject(e)
            }

        });
    }

    const loadDeliveryMethods = async () => {
        return new Promise((resolve, reject) => {
            api().post("/site/cart/delivery-method/", { cart: uuid.value })
                .then(response => {
                    deliveryMethods.value = response.data.delivery_methods;

                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    }
    const updateDeliveryMethod = async (payload) => {
            payload.cart = uuid.value;
            return new Promise((resolve, reject) => {
                api().post("/site/cart/delivery-method/update/", payload)
                    .then(response => {
                        loadCart();
                        resolve(response);
                        // mitt.$emit('after-adding');
                    })
                    .catch(error => {
                        reject(error);
                    });
            });

    }

    const updateCountryCode = (deliveryCountryCode) => {
        return new Promise((resolve) => {
           delivery_country_code.value = deliveryCountryCode;
            loadCart().then(response => {
                loadDeliveryMethods();
                resolve(response.data.delivery_country_code);
            });
        });
    }

    const destroyLine = async (payload) => {
        return new Promise((resolve, reject) => {

            isLoadingFull.value = true

            payload.cart = uuid.value;
            api().delete("/site/cart/line/delete/", { data: payload })
                .then(response => {
                    loadCart();
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    }

    const validateDiscountCode = (payload) => {
        return new Promise((resolve, reject) => {
            payload.cart = uuid.value;
            api().post("/site/cart/validate-discount-code/", _.merge(payload, {_method: "PUT"}))
                .then(response => {
                    if(response.data.valid) {
                        loadCart();
                    }
                    resolve(response);
                })
                .catch(error => {
                    reject(error.response);
                });
        });
    }
    const deleteDiscountCode = (payload) => {
        return new Promise((resolve, reject) => {
            payload.cart = uuid.value;
            api().post("/site/cart/delete-discount-code/", _.merge(payload, {_method: "PUT"}))
                .then(response => {
                    loadCart();
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    }

    onMounted(async () => {
        if (uuid.value === null && window.cart_uuid) {
            uuid.value = window.cart_uuid
        }

        // Only load cart when not an iframe to prevent loading inside row-editor
        if (!isIframe()) {
            await loadCart()
        }
    })

    return {
        isLoading,
        isLoadingFull,
        cart,
        items,
        itemCount,
        deliveryMethod,
        deliveryMethods,
        delivery_country_code,
        events,
        uuid,
        getDiscounts,
        loadCart,
        updateQuantity,
        destroyLine,
        loadDeliveryMethods,
        updateDeliveryMethod,
        updateCountryCode,
        addItem,
        validateDiscountCode,
        deleteDiscountCode,
    }
});
